import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

// =============================================================================
// PR Local metrics context
// -----------------------------------------------------------------------------
// Read-only data layer for the PR Local usage-metrics dashboard — the PR Local
// counterpart of navelaContext's metrics helpers. Talks to the prlocal-metrics
// package on the admin Go backend, mounted under both /admin and /master:
//
//   {API}/{admin|master}/prlocal-metrics/api/v1/metrics/{summary,filters,
//         timeseries,experiences/clicks,screens,targets,events}
//
// Every helper is a filtered GET that aggregates the PR Local metric logs
// (prlocal_app_metrics, prlocal_map_metrics, viator_click_events). Handlers
// return the payload directly (no {success,data} envelope), but we still
// unwrap defensively so a wrapped response would also work.
// =============================================================================

const PrlocalMetricsContext = createContext();

export const usePrlocalMetrics = () => {
  const context = useContext(PrlocalMetricsContext);
  if (!context) {
    throw new Error('usePrlocalMetrics must be used within a PrlocalMetricsProvider');
  }
  return context;
};

export const PrlocalMetricsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Pick the scope for the current login (dual admin/master access).
  const scope = useCallback(() => {
    if (loginType === 'master') return 'master';
    if (loginType === 'admin') return 'admin';
    throw new Error('Invalid login type for PR Local metrics access');
  }, [loginType]);

  const metricsBase = useCallback(
    () => `${API_BASE_URL}/${scope()}/prlocal-metrics/api/v1/metrics`,
    [API_BASE_URL, scope],
  );

  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can view PR Local metrics');
    }
  }, [loginType]);

  const handleApiResponse = async (response) => {
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }
    if (!response.ok) {
      const msg = (data && (data.error || data.message)) || 'Request failed';
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const clearError = useCallback(() => setError(null), []);

  // Wrap loading/error handling around one request so each helper stays tiny.
  const run = useCallback(async (fn, fallbackMsg) => {
    validateAccess();
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      const msg = err.message || fallbackMsg;
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }, [validateAccess]);

  const buildQuery = (options = {}, keys = []) => {
    const params = new URLSearchParams();
    keys.forEach((k) => {
      const v = options[k];
      if (v !== undefined && v !== null && v !== '') params.append(k, v);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  // Handlers respond with the payload directly; unwrap a {data} envelope if one
  // ever appears so the callers don't have to care.
  const unwrap = (res) => (res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res);

  // ── Usage metrics (read-only) ────────────────────────────────────────────────
  const fetchMetricsSummary = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'platform']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/summary${qs}`)));
  }, 'Failed to fetch metrics summary'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchMetricsFilters = useCallback(() => run(async () =>
    unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/filters`))),
  'Failed to fetch metrics filters'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchMetricsTimeseries = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'interval', 'platform']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/timeseries${qs}`)));
  }, 'Failed to fetch metrics timeseries'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchExperienceClicks = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'surface', 'city', 'limit']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/experiences/clicks${qs}`)));
  }, 'Failed to fetch experience clicks'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchScreenViews = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'platform']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/screens${qs}`)));
  }, 'Failed to fetch screen views'), [run, makeAuthenticatedRequest, metricsBase]);

  const fetchTargets = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'platform', 'target_type', 'category']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/targets${qs}`)));
  }, 'Failed to fetch targets'), [run, makeAuthenticatedRequest, metricsBase]);

  // Raw event explorer — returns the full { events, total_count, ... } envelope.
  const fetchMetricsEvents = useCallback((options = {}) => run(async () => {
    const qs = buildQuery(options, ['from', 'to', 'event_name', 'event_category', 'platform', 'screen', 'target_type', 'category', 'municipality', 'search', 'limit', 'offset']);
    return unwrap(await handleApiResponse(await makeAuthenticatedRequest(`${metricsBase()}/events${qs}`)));
  }, 'Failed to fetch events'), [run, makeAuthenticatedRequest, metricsBase]);

  const value = {
    loading,
    error,
    clearError,
    fetchMetricsSummary,
    fetchMetricsFilters,
    fetchMetricsTimeseries,
    fetchExperienceClicks,
    fetchScreenViews,
    fetchTargets,
    fetchMetricsEvents,
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasPrlocalMetricsAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <PrlocalMetricsContext.Provider value={value}>
      {children}
    </PrlocalMetricsContext.Provider>
  );
};
